import React, { useState } from 'react';
import { Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { getDownloadUrl } from '../../api/projectManagementApi';
import { formatBytes } from '../../utils/attachmentUtils';

/** Pick icon name + background colour for file type */
function getFileIconInfo(attachment) {
    const name = (attachment.originalName || '').toLowerCase();
    const mime = (attachment.mimeType || '').toLowerCase();

    if (mime.includes('pdf') || name.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bg: '#fde8e8', color: '#c0392b', label: 'PDF' };
    if (mime.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.svg') || name.endsWith('.webp'))
        return { icon: 'image', bg: '#e8f4fd', color: '#2980b9', label: 'IMG' };
    if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx') || name.endsWith('.odt'))
        return { icon: 'description', bg: '#e8edf8', color: '#2b4da0', label: 'DOC' };
    if (mime.includes('sheet') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))
        return { icon: 'table_chart', bg: '#e6f4ee', color: '#1a7a45', label: 'XLS' };
    if (mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx'))
        return { icon: 'slideshow', bg: '#fef0e6', color: '#c0622b', label: 'PPT' };
    if (mime.includes('zip') || mime.includes('rar') || name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz'))
        return { icon: 'folder_zip', bg: '#fef9e6', color: '#b7950b', label: 'ZIP' };
    if (mime.includes('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv') || name.endsWith('.webm') || name.endsWith('.3gp'))
        return { icon: 'videocam', bg: '#f0e6fe', color: '#7d3c98', label: 'VID' };
    if (mime.includes('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.ogg') || name.endsWith('.flac') || name.endsWith('.aac'))
        return { icon: 'audio_file', bg: '#fde8f5', color: '#c0407a', label: 'AUD' };
    if (name.endsWith('.txt') || name.endsWith('.md'))
        return { icon: 'article', bg: '#efefef', color: '#555', label: 'TXT' };
    return { icon: 'attach_file', bg: '#efefef', color: '#666', label: 'FILE' };
}

export default function AttachmentItem({ attachment, taskId, onDelete }) {
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);
    const { icon, bg, color } = getFileIconInfo(attachment);

    const getUrl = async (disposition = 'inline') => {
        if (attachment.publicUrl) {
            return attachment.publicUrl;
        }
        const { data } = await getDownloadUrl(taskId, attachment._id, disposition);
        return data.result.downloadUrl;
    };

    const handleView = async () => {
        setViewing(true);
        try {
            const url = await getUrl('inline');
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            message.error('Could not open attachment.');
        } finally {
            setViewing(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const url = await getUrl('attachment');
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.originalName || 'attachment';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            message.error('Could not download attachment.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            onClick={handleView}
            className="flex items-center gap-2.5 p-2.5 bg-white border border-ed-surface-container-high rounded-xl w-fit cursor-pointer hover:shadow-sm transition-shadow group"
            style={{ maxWidth: 260 }}
        >
            {/* File-type icon badge */}
            <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: bg }}
            >
                <span className="material-symbols-outlined text-[20px]" style={{ color }}>
                    {icon}
                </span>
            </div>

            {/* Name + size */}
            <div className="flex-1 min-w-0">
                <div
                    className="text-xs font-semibold text-ed-on-surface truncate"
                    title={attachment.originalName}
                >
                    {attachment.originalName}
                </div>
                <div className="text-[11px] text-ed-on-surface-variant mt-0.5">
                    {formatBytes(attachment.size)}
                </div>
            </div>

            {/* Actions: Download + Delete */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1">
                <Tooltip title="Download">
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                        disabled={downloading || viewing}
                        className="text-ed-on-surface-variant hover:text-primary p-1 rounded hover:bg-ed-surface-container-low transition-colors"
                    >
                        {downloading
                            ? <LoadingOutlined style={{ fontSize: 13 }} />
                            : <span className="material-symbols-outlined text-sm">download</span>
                        }
                    </button>
                </Tooltip>
                <Popconfirm
                    title="Remove attachment?"
                    onConfirm={(e) => { e.stopPropagation(); onDelete(attachment._id); }}
                    onCancel={(e) => e.stopPropagation()}
                    okText="Remove"
                    okButtonProps={{ danger: true, size: 'small' }}
                    cancelText="Cancel"
                    placement="topRight"
                >
                    <Tooltip title="Remove">
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className="text-ed-on-surface-variant hover:text-error p-1 rounded hover:bg-red-50 transition-colors"
                        >
                            <DeleteOutlined style={{ fontSize: 13 }} />
                        </button>
                    </Tooltip>
                </Popconfirm>
            </div>
        </div>
    );
}
